import React from 'react'
import styled from 'styled-components'

import ChartsSvgIcon from 'components/icons/charts-svg-icon/ChartsSvgIcon'
import FeedbackSvgIcon from 'components/icons/feedback-svg-icon/FeedbackSvgIcon'
import KeywordsSvgIcon from 'components/icons/keywords-svg-icon/KeywordsSvgIcon'
import { TooltipIconButton } from 'components/TooltipIconButton'
import type { CatalogDetailHeaderLink } from 'features/catalog/features/detail/hooks/use-catalog-detail-header-links'
import { CHARTS_2_URL } from 'features/charts-v2/paths'
import { FEEDBACK_URL } from 'features/feedback/paths'
import { KEYWORDS_URL } from 'features/keywords/paths'

const ACTION_BY_PAGE: Record<
  string,
  { label: string; icon: React.ReactElement }
> = {
  [FEEDBACK_URL]: { label: 'View in Feedback', icon: <FeedbackSvgIcon /> },
  [CHARTS_2_URL]: { label: 'View in Charts', icon: <ChartsSvgIcon /> },
  [KEYWORDS_URL]: { label: 'View in Keywords', icon: <KeywordsSvgIcon /> },
}

type Props = {
  /** Feedback/Charts/Keywords links, in that display order — resolved by
   * `useCatalogDetailHeaderLinks` in `CatalogDetail` (features/detail/
   * Container.tsx) and passed down so this stays a pure view (no data
   * hooks — see `view-data-hooks`/`view-service-hooks`). */
  links: CatalogDetailHeaderLink[]
  projectId: string
}

/**
 * Compact "jump to this item's filtered view" row for the detail page's
 * title header — each button opens the matching page pre-filtered to the
 * current product/brand/theme/source, once its link has resolved.
 */
export const CatalogDetailHeaderActions: React.FC<Props> = ({
  links,
  projectId,
}) => (
  <ActionsRow>
    {links.map(({ step, link }) => {
      const action = ACTION_BY_PAGE[step.page]
      return (
        <TooltipIconButton
          key={step.page}
          tooltipTitle={action?.label ?? step.title}
          icon={action?.icon ?? <FeedbackSvgIcon />}
          height={32}
          width={32}
          ariaLabel={action?.label ?? step.title}
          disabled={!link}
          onClick={() => {
            if (!link) return
            window.open(`/project/${projectId}/${step.page}/${link}`, '_blank')
          }}
        />
      )
    })}
  </ActionsRow>
)

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`
